import { TableContainer, TableContents, TableTop } from "style/admin/table";
import { NoticeDto } from "interface/admin/interface.contents";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import PaginationControls from "components/admin/table.components/pagination";
import { formatDateTime, formatNumber } from "utils/util";

interface DataProps {
  data: NoticeDto[];
}

const AdminNoticeTable = ({ data }: DataProps) => {
  const navigate = useNavigate();
  // ✅ 최신 등록일(`createdAt`)을 기준으로 내림차순 정렬
  const sortedData = useMemo(() => {
    return [...data].sort(
      (a, b) => new Date(b.regDt).getTime() - new Date(a.regDt).getTime()
    );
  }, [data]);

  const columns = useMemo<ColumnDef<NoticeDto>[]>(
    () => [
      {
        header: "Index",
        id: "number", // 넘버링을 위한 id
        cell: ({ row }) => sortedData.length - row.index, // 각 행의 인덱스에 1을 더해 넘버링
        size: 120,
      },
      {
        header: "제목",
        accessorKey: "title",
        cell: (info) => {
          const value = info.getValue();
          return (
            <div
              className="title-box"
              onClick={() =>
                clickedRowHandler(info.row.original.announcementId)
              }
            >
              {String(value)}
            </div>
          );
        },
        size: 479,
      },
      {
        header: "조회수",
        accessorKey: "views",
        cell: (info) => formatNumber(Number(info.getValue())),
        size: 160,
      },
      {
        header: "작성자",
        accessorKey: "regUserId",
        cell: (info) => info.getValue(),
        size: 160,
      },
      {
        header: "작성일시",
        accessorKey: "regDt",
        cell: (info) => {
          const value = info.getValue(); // ✅ 값 가져오기
          return value ? formatDateTime(String(value)) : "-"; // ✅ 값이 없으면 "-" 반환
        },
        size: 160,
      },
      {
        header: "게시상태",
        accessorKey: "useYn",
        cell: (info) => {
          const value = info.getValue(); // ✅ 값 가져오기
          return value === "Y" ? (
            <div className="bluebox hide-item">정상노출</div>
          ) : (
            <div className="redbox hide-item">숨겨짐</div>
          );
        },
        size: 96,
      },
    ],
    [sortedData]
  );

  const table = useReactTable({
    data: sortedData,
    columns,
    enableRowSelection: true,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageSize: 50 } }, // ✅ 초기 페이지 크기 설정
  });

  const paginationState = table.getState().pagination;
  const startRow = paginationState.pageIndex * paginationState.pageSize + 1;
  const endRow = startRow + table.getRowModel().rows.length - 1;
  const totalRows = sortedData.length;

  const clickedRowHandler = (id: number) => {
    navigate(`/admin/notice/detail/${id}`);
  };

  return (
    <TableContainer>
      <TableTop>
        <span className="table-count">
          {totalRows} 중 {startRow}-{endRow}
        </span>
        <PaginationControls table={table} />
      </TableTop>
      <TableContents>
        <table
          className={
            table.getRowModel().rows.length === 0 ? "no-data-table" : ""
          }
        >
          <thead>
            {table.getHeaderGroups().map((item) => (
              <tr key={item.id}>
                {item.headers.map((header) => (
                  <th
                    key={header.id}
                    style={{ minWidth: `${header.getSize()}px` }}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.length > 0 ? (
              table.getRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  onDoubleClick={() =>
                    clickedRowHandler(row.original.announcementId)
                  } // ✅ 더블 클릭 시 상세 페이지 이동
                  style={{ cursor: "pointer" }} // ✅ 마우스 오버 시 클릭 가능하도록 스타일 추가
                >
                  {row.getVisibleCells().map((cell) => {
                    const isHideYn = cell.column.id === "useYn";
                    const isTitle = cell.column.id === "title";
                    const isProfile = cell.column.id === "userName";

                    return (
                      <td
                        key={cell.id}
                        className={`${isHideYn ? "hide-state" : ""} ${
                          isTitle ? "notice-title-cell" : ""
                        } ${isProfile ? "profile-cell" : ""}`.trim()} // ✅ 여러 개의 클래스 추가
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))
            ) : (
              // ✅ 데이터가 없을 경우 "검색 결과가 없습니다" 표시
              <tr className="no-data">
                <td colSpan={columns.length} className="no-data-td">
                  검색 결과가 없습니다.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </TableContents>
    </TableContainer>
  );
};

export default AdminNoticeTable;
